'use client';

import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { BsCloudUpload, BsFileEarmarkMusic, BsX, BsPlus } from 'react-icons/bs';
import { useSoundboard } from '@/contexts/SoundboardManager';

interface AudioFileDropZoneProps {
  onFileDrop: (file: File) => void;
  maxSize?: number; // in bytes, default 50MB
  acceptedTypes?: string[]; // default ['audio/mpeg', 'audio/wav']
  className?: string;
  disabled?: boolean;
  addToSoundboard?: boolean; // Whether to add files to soundboard
}

const AudioFileDropZone: React.FC<AudioFileDropZoneProps> = ({
  onFileDrop,
  maxSize = 50 * 1024 * 1024, // 50MB
  acceptedTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3'],
  className = '',
  disabled = false,
  addToSoundboard = true
}) => {
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isAddingToSoundboard, setIsAddingToSoundboard] = useState(false);
  
  const { addItem, error: soundboardError } = useSoundboard();

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        toast.error(`File is too large. Maximum size is ${Math.round(maxSize / (1024 * 1024))}MB`);
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        toast.error('Invalid file type. Please upload MP3 or WAV files only.');
      } else {
        toast.error('File upload failed. Please try again.');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setUploadProgress(0);
      
      // Add to soundboard if enabled
      if (addToSoundboard) {
        setIsAddingToSoundboard(true);
        try {
          await addItem(file);
          toast.success(`"${file.name}" added to soundboard`);
        } catch (err) {
          console.error('Error adding to soundboard:', err);
          toast.error('Failed to add to soundboard');
        } finally {
          setIsAddingToSoundboard(false);
        }
      }
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev === null || prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 10;
        });
      }, 100);

      // Call the onFileDrop callback after a short delay
      setTimeout(() => {
        onFileDrop(file);
        setUploadProgress(null);
        clearInterval(progressInterval);
      }, 1000);
    }
  }, [onFileDrop, maxSize, addToSoundboard, addItem]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav'],
      'audio/mp3': ['.mp3']
    },
    maxSize,
    multiple: false,
    disabled
  });

  const removeFile = useCallback(() => {
    setSelectedFile(null);
    setUploadProgress(null);
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-all duration-200 ease-in-out
          ${isDragActive && !isDragReject 
            ? 'border-blue-400 bg-blue-50/10' 
            : isDragReject 
            ? 'border-red-400 bg-red-50/10' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50/5'}
        `}
      >
        <input {...getInputProps()} />
        
        {selectedFile ? (
          <div className="space-y-3">
            <div className="flex items-center justify-center space-x-2">
              <BsFileEarmarkMusic className="w-8 h-8 text-blue-400" />
              <div className="text-left">
                <p className="text-sm font-medium text-white truncate max-w-48">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-400">
                  {formatFileSize(selectedFile.size)}
                </p>
                {isAddingToSoundboard && (
                  <div className="flex items-center space-x-1 text-xs text-green-400">
                    <BsPlus className="w-3 h-3" />
                    <span>Adding to soundboard...</span>
                  </div>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile();
                }}
                className="text-gray-400 hover:text-white transition-colors"
                title="Remove file"
                aria-label="Remove file"
              >
                <BsX className="w-5 h-5" />
              </button>
            </div>
            
            {uploadProgress !== null && (
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` } as React.CSSProperties}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <BsCloudUpload className={`w-12 h-12 mx-auto ${
              isDragActive ? 'text-blue-400' : 'text-gray-400'
            }`} />
            
            <div>
              <p className="text-sm text-white">
                {isDragActive 
                  ? 'Drop the audio file here...' 
                  : 'Drag & drop an audio file here, or click to select'
                }
              </p>
              <p className="text-xs text-gray-400 mt-1">
                MP3, WAV files up to {Math.round(maxSize / (1024 * 1024))}MB
              </p>
            </div>
          </div>
        )}
      </div>
      
      {isDragReject && (
        <p className="text-red-400 text-xs mt-2 text-center">
          Invalid file type. Please upload MP3 or WAV files only.
        </p>
      )}
    </div>
  );
};

export default AudioFileDropZone;
